import { useState, useRef, useEffect } from 'react';
import { uploadRecord } from '../api/jueGuangService';
import { buildNameOptions } from '@shared/components/CommenterNameSelect';
import toast from 'react-hot-toast';
import { useLanguage } from '@shared/contexts/LanguageContext';

const MAX_VIDEO_SEC = 30;

function formatDate(date) {
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const y = date.getFullYear();
  return `${m}. ${d}. ${y}`;
}

export default function SubmitPostModal({ open, onClose, onSuccess, user, topicId }) {
  const { t } = useLanguage();
  const [displayName, setDisplayName] = useState('');
  const [nameOptions, setNameOptions] = useState([]);
  const [nameDropOpen, setNameDropOpen] = useState(false);
  const nameDropRef = useRef(null);
  const [description, setDescription] = useState('');
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [mediaType, setMediaType] = useState(null); // 'image' | 'video'
  const [durationError, setDurationError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadMenuOpen, setUploadMenuOpen] = useState(false);
  const uploadMenuRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraPhotoRef = useRef(null);
  const cameraVideoRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!nameDropOpen) return;
    function handleClick(e) {
      if (nameDropRef.current && !nameDropRef.current.contains(e.target)) setNameDropOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [nameDropOpen]);

  useEffect(() => {
    if (!uploadMenuOpen) return;
    function handleClick(e) {
      if (uploadMenuRef.current && !uploadMenuRef.current.contains(e.target)) setUploadMenuOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
    };
  }, [uploadMenuOpen]);

  useEffect(() => {
    if (!open) return;
    const opts = buildNameOptions({
      name: user?.name ?? '',
      dharma: user?.dharmaName ?? '',
      temple: user?.monasteryName ?? '',
    });
    setNameOptions(opts);
    setDisplayName(opts[0]?.value ?? '');
    setDescription('');
    setMediaFile(null);
    setMediaPreview(null);
    setMediaType(null);
    setDurationError(false);
  }, [open, user]);

  function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type.startsWith('video/')) {
      const url = URL.createObjectURL(file);
      const vid = document.createElement('video');
      vid.preload = 'metadata';
      vid.onloadedmetadata = () => {
        if (vid.duration > MAX_VIDEO_SEC) {
          URL.revokeObjectURL(url);
          setDurationError(true);
          setMediaFile(null);
          setMediaPreview(null);
          setMediaType(null);
          e.target.value = '';
          return;
        }
        setDurationError(false);
        setMediaFile(file);
        setMediaPreview(url);
        setMediaType('video');
      };
      vid.src = url;
    } else {
      setDurationError(false);
      setMediaFile(file);
      setMediaPreview(URL.createObjectURL(file));
      setMediaType('image');
    }
  }

  function removeMedia() {
    setMediaFile(null);
    setMediaPreview(null);
    setMediaType(null);
    setDurationError(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function handleSubmit() {
    if (!displayName.trim()) return toast.error(t('jue-guang-topic.selectDisplayName'));
    if (!description.trim()) return toast.error(t('jue-guang-topic.fillContent'));
    setSubmitting(true);
    const res = await uploadRecord({
      username: displayName,
      topic_id: topicId,
      description,
      image_file: mediaType === 'image' ? mediaFile : null,
      video_file: mediaType === 'video' ? mediaFile : null,
    });
    setSubmitting(false);
    if (res.success) {
      toast.success(t('jue-guang-topic.shareSuccessPending'));
      onSuccess?.();
      onClose?.();
    } else {
      toast.error(res.error ?? t('jue-guang-topic.shareFailed'));
    }
  }

  if (!open) return null;

  const today = formatDate(new Date());
  const isSubmittable = displayName.trim() && description.trim() && !submitting;
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/.test(navigator.userAgent);
  const isMobile = isIOS || isAndroid;

  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ backgroundImage: 'url(/image/jue-guang/background.png)', backgroundSize: 'cover', backgroundPosition: 'top' }}>

      {/* ── Status bar placeholder ── */}
      <div className="h-14 shrink-0 flex items-center px-5">
        <div className="flex-1" />
        <button
          type="button"
          onClick={onClose}
          className="w-7 h-7 flex items-center justify-center cursor-pointer"
        >
          <span className="material-symbols-outlined text-black" style={{ fontSize: '20px' }}>close</span>
        </button>
      </div>

      {/* ── Title ── */}
      <div className="flex flex-col items-center pb-3 shrink-0">
        <h1 className="text-2xl font-normal font-['Noto_Serif_TC'] tracking-[4px] text-black leading-7">{t('jue-guang.pageTitle')}</h1>
      </div>

      {/* ── Scrollable body ── */}
      <div className="flex-1 overflow-y-auto">
      <div className="w-full max-w-[600px] mx-auto">

      {/* ── Media area ── */}
      <div className={`w-full bg-gradient-to-br from-[#f9f9f9] to-[#d2d2d2] relative flex items-center justify-center ${mediaPreview ? 'max-h-[70vh] overflow-hidden' : 'aspect-square'}`}>
        {mediaPreview ? (
          <>
            {mediaType === 'video'
              ? <video src={mediaPreview} controls controlsList="nodownload" onContextMenu={(e) => e.preventDefault()} className="w-full h-full object-contain" />
              : <img src={mediaPreview} alt="" className="w-full h-full object-cover" />
            }
            <button
              type="button"
              onClick={removeMedia}
              className="absolute top-3 right-3 w-8 h-8 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center cursor-pointer transition-colors"
            >
              <span className="material-symbols-outlined text-white" style={{ fontSize: '16px' }}>close</span>
            </button>
          </>
        ) : (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-4"
            ref={uploadMenuRef}
            onClick={() => isAndroid ? setUploadMenuOpen(o => !o) : (!uploadMenuOpen && fileInputRef.current?.click())}
          >
            <span
              className="material-symbols-outlined text-white/60"
              style={{ fontSize: '64px', fontVariationSettings: "'FILL' 0" }}
            >panorama</span>

            {!isMobile ? (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-sm text-white/80 cursor-pointer"
              >
                {t('jue-guang-topic.clickToUpload')}
              </button>
            ) : isIOS ? (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-sm text-white/80 cursor-pointer"
              >
                {t('jue-guang-topic.clickToUpload')}
              </button>
            ) : (
              <div className="relative" onClick={e => e.stopPropagation()}>
                <button
                  type="button"
                  onClick={() => setUploadMenuOpen(o => !o)}
                  className="text-sm text-white/80 cursor-pointer"
                >
                  {t('jue-guang-topic.clickToUpload')}
                </button>
                {uploadMenuOpen && (
                  <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-white rounded-xl shadow-lg overflow-hidden min-w-[120px]">
                    <button
                      type="button"
                      onClick={() => { fileInputRef.current?.click(); setUploadMenuOpen(false); }}
                      className="w-full flex items-center gap-2 px-4 py-3 text-sm text-stone-700 hover:bg-stone-50 cursor-pointer"
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>photo_library</span>
                      {t('jue-guang-topic.photoAlbum')}
                    </button>
                    <button
                      type="button"
                      onClick={() => { cameraPhotoRef.current?.click(); setUploadMenuOpen(false); }}
                      className="w-full flex items-center gap-2 px-4 py-3 text-sm text-stone-700 hover:bg-stone-50 cursor-pointer"
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>camera_alt</span>
                      {t('jue-guang-topic.takePhoto')}
                    </button>
                    <button
                      type="button"
                      onClick={() => { cameraVideoRef.current?.click(); setUploadMenuOpen(false); }}
                      className="w-full flex items-center gap-2 px-4 py-3 text-sm text-stone-700 hover:bg-stone-50 cursor-pointer"
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>videocam</span>
                      {t('jue-guang-topic.recordVideo')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {durationError && (
        <p className="text-xs text-red-500 px-5 pt-2 flex items-center gap-1 shrink-0">
          <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>error</span>
          {t('jue-guang-topic.durationError')}
        </p>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*"
        className="hidden"
        onChange={handleFileChange}
      />
      <input
        ref={cameraPhotoRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFileChange}
      />
      <input
        ref={cameraVideoRef}
        type="file"
        accept="video/*"
        capture="environment"
        className="hidden"
        onChange={handleFileChange}
      />

      {/* ── Info area ── */}
      <div className="px-[39px] pt-4 pb-6 flex flex-col gap-3">

        {/* Date */}
        <p className="text-sm text-[#777777] font-['SF_Pro'] leading-7">{today}</p>

        {/* User row */}
        <div className="flex items-center gap-1.5 min-w-0" ref={nameDropRef}>
          <div className="w-6 h-6 rounded-full bg-[#c5c5c5] overflow-hidden shrink-0 flex items-center justify-center">
            {user?.avatar
              ? <img src={user.avatar} alt="" className="w-6 h-6 object-cover" />
              : <span className="material-symbols-outlined text-white" style={{ fontSize: '14px' }}>person</span>
            }
          </div>
          {nameOptions.length > 1 ? (
            <div className="relative">
              <button
                type="button"
                onClick={() => setNameDropOpen(o => !o)}
                className="flex items-center gap-1 cursor-pointer"
              >
                <span className="text-base font-['SF_Pro'] text-black leading-7">{displayName}</span>
                <span className="material-symbols-outlined text-stone-400" style={{ fontSize: '16px' }}>unfold_more</span>
              </button>
              {nameDropOpen && (
                <div className="absolute left-0 top-full mt-1 bg-white rounded-xl shadow-lg border border-[#E8DCC8] py-1 z-20 min-w-[160px]">
                  {nameOptions.map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => { setDisplayName(opt.value); setNameDropOpen(false); }}
                      className={`w-full text-left px-4 py-2 text-sm cursor-pointer transition-colors ${opt.value === displayName ? 'text-[#ffa800] font-semibold bg-stone-50' : 'text-black hover:bg-stone-50'}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <span className="text-base font-['SF_Pro'] text-black leading-7 truncate">{displayName}</span>
          )}
        </div>

        {/* Description */}
        <textarea
          ref={textareaRef}
          value={description}
          onChange={e => {
            if (e.target.value.length > 10) return;
            setDescription(e.target.value);
            const el = textareaRef.current;
            if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; }
          }}
          placeholder={t('jue-guang-topic.sharePlaceholder')}
          rows={4}
          className="w-full text-base font-normal text-[#777777] placeholder-[#777777] bg-transparent outline-none leading-6 resize-none overflow-hidden"
        />
        <p className="text-xs text-[#aaaaaa] text-left">{description.length}/10</p>
      </div>

      {/* ── Divider ── */}
      <div className="mx-[30px] h-px bg-black" />

      {/* ── Footer ── */}
      <div className="px-5 py-4 flex items-center justify-end">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!isSubmittable}
          className="w-20 h-9 flex items-center justify-center text-white text-base font-bold font-['SF_Pro'] tracking-widest transition-opacity cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: '#ffa800' }}
        >
          {submitting ? t('jue-guang-topic.submitting') : t('jue-guang-topic.publish')}
        </button>
      </div>

      </div>{/* end max-width wrapper */}
      </div>{/* end scrollable body */}

    </div>
  );
}
